import React, { useState, useEffect } from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { formatCurrency, convertFromMXN } from '../utils/formatters';
import AnimatedNumberDisplay from './ui/AnimatedNumberDisplay';
import { getDetailedCertificateEvolution } from '../utils/calculations/certificateEvolution';
import { getLanguageContent, getLanguageFromCurrency } from '../data/languages';
import { Save, Download, Eye, EyeOff, Settings, ChevronDown, ChevronUp, BarChart3, DollarSign, Calendar, Target, TrendingUp, Activity, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

interface ExcelTableV5Props {
  initialCertificates?: number;
  years?: number;
  hideAdditionalOptions?: boolean;
}

const ExcelTableV5: React.FC<ExcelTableV5Props> = ({ 
  initialCertificates, 
  years, 
  hideAdditionalOptions = false 
}) => {
  const { investment } = useCalculator();
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);
  
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    year: true,
    date: true,
    certificatePrice: true,
    certificates: true,
    totalUtility: true,
    reinvestmentFund: true,
    availableForReinvestment: true,
    events: true,
    payments: false,
    totalCertificates: false,
    certificatesFromReinvestment: false,
    citrusPatrimony: false,
    citrusIncome: false,
    yearlyCashOutAmount: false,
    cumulativeCashOutAmount: false,
    yearlyReinvestmentContribution: false,
    cumulativeReinvestmentContribution: false,
    cumulativeTotalUtility: false,
    paymentBoostActive: false,
    paymentBoostAmount: false
  });

  // Use props if provided, otherwise use context values
  const effectiveInitialCertificates = initialCertificates || investment.initialCertificates;
  const effectiveYears = years || investment.years;

  // Get detailed certificate evolution
  const certificateEvolution = getDetailedCertificateEvolution(
    investment, 
    effectiveInitialCertificates, 
    effectiveYears
  );

  // Function to convert MXN values to display currency
  const convertToDisplayCurrency = (mxnValue: number): number => {
    return convertFromMXN(mxnValue, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
  };

  const toggleColumn = (column: keyof typeof selectedColumns) => {
    setSelectedColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const toggleAllColumns = () => {
    const newValue = !showAllColumns;
    setShowAllColumns(newValue);
    
    if (newValue) {
      // Show all columns
      setSelectedColumns(Object.keys(selectedColumns).reduce((acc, key) => ({
        ...acc,
        [key]: true
      }), {} as typeof selectedColumns));
    } else {
      // Show only essential columns
      setSelectedColumns({
        year: true,
        date: true,
        certificatePrice: true,
        certificates: true,
        totalUtility: true,
        reinvestmentFund: true,
        availableForReinvestment: true,
        events: true,
        payments: false,
        totalCertificates: false,
        certificatesFromReinvestment: false,
        citrusPatrimony: false,
        citrusIncome: false,
        yearlyCashOutAmount: false,
        cumulativeCashOutAmount: false,
        yearlyReinvestmentContribution: false,
        cumulativeReinvestmentContribution: false,
        cumulativeTotalUtility: false,
        paymentBoostActive: false,
        paymentBoostAmount: false
      });
    }
  };

  const saveTable = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8">
          <title>Evolución Detallada de Certificados - Cosecha Capital</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              line-height: 1.5;
              margin: 0;
              padding: 20px;
              max-width: 1400px;
              margin: 0 auto;
              color: #1f2937;
            }
            h1 {
              color: #16a34a;
              margin-bottom: 20px;
              text-align: center;
            }
            p {
              color: #4b5563;
              margin-bottom: 30px;
              text-align: center;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              border-radius: 8px;
              overflow: hidden;
            }
            th, td {
              padding: 8px 12px;
              text-align: left;
              border-bottom: 1px solid #e5e7eb;
              font-size: 0.875rem;
            }
            th {
              background: #16a34a;
              color: white;
              font-weight: 500;
              position: sticky;
              top: 0;
              z-index: 10;
            }
            tr:nth-child(even) {
              background: #f9fafb;
            }
            tr.highlight {
              background: #dcfce7;
            }
            .certificate {
              display: inline-block;
              width: 24px;
              height: 24px;
              line-height: 24px;
              text-align: center;
              border-radius: 50%;
              margin: 0 2px;
              font-size: 12px;
              font-weight: 500;
            }
            .certificate.reserved {
              background: #e0f2fe;
              color: #0277bd;
            }
            .certificate.waiting {
              background: #e5e7eb;
              color: #4b5563;
            }
            .certificate.growing {
              background: #fef3c7;
              color: #92400e;
            }
            .certificate.producing {
              background: #dcfce7;
              color: #166534;
            }
            .notes {
              background-color: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin-top: 30px;
            }
            .notes h3 {
              color: #374151;
              margin-bottom: 10px;
            }
            .notes ul {
              color: #4b5563;
              padding-left: 20px;
            }
            .event-new {
              color: #16a34a;
              font-weight: 500;
            }
            .event-reserved {
              color: #0d9488;
              font-weight: 500;
            }
            .payment-info {
              font-size: 0.75rem;
              color: #6b7280;
            }
          </style>
        </head>
        <body>
          <h1>Evolución Detallada de Certificados - Cosecha Capital</h1>
          <p>Análisis detallado de la evolución de certificados durante ${effectiveYears} años de inversión con ${effectiveInitialCertificates} certificados iniciales.</p>
          
          <table>
            <thead>
              <tr>
                ${selectedColumns.year ? '<th>Año</th>' : ''}
                ${selectedColumns.date ? '<th>Fecha</th>' : ''}
                ${selectedColumns.certificatePrice ? '<th>Precio Cert.</th>' : ''}
                ${selectedColumns.certificates ? '<th>Certificados</th>' : ''}
                ${selectedColumns.totalUtility ? '<th>Utilidad Total</th>' : ''}
                ${selectedColumns.reinvestmentFund ? '<th>Fondo Reinversión</th>' : ''}
                ${selectedColumns.availableForReinvestment ? '<th>Saldo Disponible</th>' : ''}
                ${selectedColumns.events ? '<th>Eventos</th>' : ''}
                ${selectedColumns.payments ? '<th>Pagos</th>' : ''}
                ${selectedColumns.totalCertificates ? '<th>Total Certificados</th>' : ''}
                ${selectedColumns.certificatesFromReinvestment ? '<th>Por Reinversión</th>' : ''}
                ${selectedColumns.citrusPatrimony ? '<th>Patrimonio</th>' : ''}
                ${selectedColumns.citrusIncome ? '<th>Ingreso Anual</th>' : ''}
                ${selectedColumns.yearlyCashOutAmount ? '<th>Retiro Anual</th>' : ''}
                ${selectedColumns.cumulativeCashOutAmount ? '<th>Retiro Acumulado</th>' : ''}
                ${selectedColumns.yearlyReinvestmentContribution ? '<th>Reinversión Anual</th>' : ''}
                ${selectedColumns.cumulativeReinvestmentContribution ? '<th>Reinversión Acumulada</th>' : ''}
                ${selectedColumns.cumulativeTotalUtility ? '<th>Utilidad Acumulada</th>' : ''}
                ${selectedColumns.paymentBoostActive ? '<th>Boost Activo</th>' : ''}
                ${selectedColumns.paymentBoostAmount ? '<th>Monto Boost</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${certificateEvolution.map(row => `
                <tr class="${row.newCertificateIds.length > 0 ? 'highlight' : ''}">
                  ${selectedColumns.year ? `<td>${row.year}</td>` : ''}
                  ${selectedColumns.date ? `<td>${row.date}</td>` : ''}
                  ${selectedColumns.certificatePrice ? `<td>${formatCurrency(convertToDisplayCurrency(row.certificatePrice), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.certificates ? `
                    <td>
                      ${row.certificates.sort((a, b) => a.id - b.id).map(cert => `
                        <span class="certificate ${cert.status}" title="${cert.details}">${cert.id}</span>
                      `).join('')}
                    </td>
                  ` : ''}
                  ${selectedColumns.totalUtility ? `<td>${formatCurrency(convertToDisplayCurrency(row.totalUtility), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.reinvestmentFund ? `<td>${formatCurrency(convertToDisplayCurrency(row.reinvestmentFund), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.availableForReinvestment ? `<td>${formatCurrency(convertToDisplayCurrency(row.availableForReinvestment), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.events ? `
                    <td>
                      ${row.newCertificateIds.map(id => `
                        <div class="event-new">¡Certificado ${id} liquidado completamente!</div>
                      `).join('')}
                      ${row.reservedCertificateIds.map(id => `
                        <div class="event-reserved">Apartado certificado ${id}</div>
                      `).join('')}
                      ${row.payments.length > 0 ? `
                        <div class="payment-info">Pagos realizados: ${row.payments.length}</div>
                      ` : ''}
                    </td>
                  ` : ''}
                  ${selectedColumns.payments ? `
                    <td>
                      ${row.payments.map(payment => `
                        <div class="payment-info">Cert. ${payment.id}: ${formatCurrency(convertToDisplayCurrency(payment.amount), investment.currencyFormat)}</div>
                      `).join('')}
                    </td>
                  ` : ''}
                  ${selectedColumns.totalCertificates ? `<td>${row.totalCertificates}</td>` : ''}
                  ${selectedColumns.certificatesFromReinvestment ? `<td>${row.certificatesFromReinvestment}</td>` : ''}
                  ${selectedColumns.citrusPatrimony ? `<td>${formatCurrency(convertToDisplayCurrency(row.citrusPatrimony), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.citrusIncome ? `<td>${formatCurrency(convertToDisplayCurrency(row.citrusIncome), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.yearlyCashOutAmount ? `<td>${formatCurrency(convertToDisplayCurrency(row.yearlyCashOutAmount), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.cumulativeCashOutAmount ? `<td>${formatCurrency(convertToDisplayCurrency(row.cumulativeCashOutAmount), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.yearlyReinvestmentContribution ? `<td>${formatCurrency(convertToDisplayCurrency(row.yearlyReinvestmentContribution), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.cumulativeReinvestmentContribution ? `<td>${formatCurrency(convertToDisplayCurrency(row.cumulativeReinvestmentContribution), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.cumulativeTotalUtility ? `<td>${formatCurrency(convertToDisplayCurrency(row.cumulativeTotalUtility), investment.currencyFormat)}</td>` : ''}
                  ${selectedColumns.paymentBoostActive ? `<td>${row.paymentBoostActive ? 'Sí' : 'No'}</td>` : ''}
                  ${selectedColumns.paymentBoostAmount ? `<td>${formatCurrency(convertToDisplayCurrency(row.paymentBoostAmount), investment.currencyFormat)}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="notes">
            <h3>Leyenda de Estados:</h3>
            <ul>
              <li><span class="certificate reserved" style="background: #e0f2fe; color: #0277bd;">●</span> En proceso de pago - Muestra el monto pagado y pendiente</li>
              <li><span class="certificate waiting" style="background: #e5e7eb; color: #4b5563;">●</span> En maduración - Indica años restantes para producción</li>
              <li><span class="certificate growing" style="background: #fef3c7; color: #92400e;">●</span> Iniciando producción - Primer año de utilidades</li>
              <li><span class="certificate producing" style="background: #dcfce7; color: #166534;">●</span> Generando utilidades - Muestra años en producción y utilidad actual</li>
            </ul>
            
            <h3>Notas Importantes:</h3>
            <ul>
              <li>Los primeros ${investment.isLongTermCalculator ? '6' : '5'} años corresponden al período de maduración de los certificados.</li>
              <li>A partir del año ${investment.isLongTermCalculator ? '6' : '5'} se comienzan a generar utilidades por la operación.</li>
              <li>El precio del certificado se aprecia un ${investment.appreciationRate}% anual durante los primeros 5 años.</li>
              ${investment.increaseLemonPrice ? 
                `<li>Se considera un incremento anual del ${investment.lemonPriceIncrease}% en el precio del limón a partir del año 6.</li>` : 
                '<li>No se considera incremento en el precio del limón.</li>'
              }
              <li>La utilidad anual se calcula con base en ${investment.averageProductionPerHectare.toLocaleString()} kg/ha × $${investment.averageSalePricePerKg}/kg × 0.1 × 0.65</li>
              ${investment.reinvestProfits ? 
                '<li>Se reinvierten las utilidades para adquirir nuevos certificados automáticamente.</li>' : 
                '<li>Las utilidades se reciben como ingreso mensual sin reinversión.</li>'
              }
              ${investment.partialCashOut ? 
                `<li>Se realiza un retiro parcial del ${investment.cashOutPercentage}% de las utilidades generadas.</li>` : 
                ''
              }
              ${investment.enablePaymentBoost ? 
                '<li>El boost de pago está activado para acelerar la adquisición de certificados.</li>' : 
                ''
              }
              <li>Todos los valores están expresados en ${investment.currencyFormat}.</li>
              ${investment.currencyFormat !== 'MXN' ? 
                `<li>Tipo de cambio utilizado: ${investment.currencyFormat === 'USD' ? investment.exchangeRate : investment.exchangeRateEUR} MXN por 1 ${investment.currencyFormat}.</li>` : 
                ''
              }
            </ul>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evolucion_detallada_certificados_${effectiveInitialCertificates}_certificados_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-green-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            {content.excel.title}
          </h2>
          <p className="text-gray-600 mt-1">
            {content.excel.subtitle
              .replace('{certificates}', effectiveInitialCertificates.toString())
              .replace('{years}', effectiveYears.toString())}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={saveTable}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title="Guardar tabla como HTML"
          >
            <Save className="w-5 h-5" />
            <span>{content.common.buttons.save}</span>
          </button>
        </div>
      </div>

      {/* Configuration Panel */}
      {(
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => setIsConfigExpanded(!isConfigExpanded)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-t-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-green-600" />
              <span className="font-medium text-gray-700">{content.excel.configTitle}</span>
            </div>
            {isConfigExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {isConfigExpanded && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">{content.excel.showAllColumns}</span>
                <button
                  onClick={toggleAllColumns}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showAllColumns ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showAllColumns ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries({
                  year: content.excel.columns.year,
                  date: content.excel.columns.date,
                  certificatePrice: content.excel.columns.certificatePrice,
                  certificates: content.excel.columns.certificates,
                  totalUtility: content.excel.columns.totalUtility,
                  reinvestmentFund: content.excel.columns.reinvestmentFund,
                  availableForReinvestment: content.excel.columns.availableForReinvestment,
                  events: content.excel.columns.events,
                  payments: content.excel.columns.payments,
                  totalCertificates: content.excel.columns.totalCertificates,
                  certificatesFromReinvestment: content.excel.columns.certificatesFromReinvestment,
                  citrusPatrimony: content.excel.columns.citrusPatrimony,
                  citrusIncome: content.excel.columns.citrusIncome,
                  yearlyCashOutAmount: content.excel.columns.yearlyCashOutAmount,
                  cumulativeCashOutAmount: content.excel.columns.cumulativeCashOutAmount,
                  yearlyReinvestmentContribution: content.excel.columns.yearlyReinvestmentContribution,
                  cumulativeReinvestmentContribution: content.excel.columns.cumulativeReinvestmentContribution,
                  cumulativeTotalUtility: content.excel.columns.cumulativeTotalUtility,
                  paymentBoostActive: content.excel.columns.paymentBoostActive,
                  paymentBoostAmount: content.excel.columns.paymentBoostAmount
                }).map(([key, label]) => (
                  <label key={key} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedColumns[key as keyof typeof selectedColumns]}
                      onChange={() => toggleColumn(key as keyof typeof selectedColumns)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-green-600" />
            <h3 className="font-medium text-green-800">Certificados Iniciales</h3>
          </div>
          <p className="text-2xl font-bold text-green-700 relative overflow-hidden">
            <AnimatedNumberDisplay
              value={effectiveInitialCertificates}
              currencyFormat="MXN"
              duration={1.2}
              staggerDelay={0.08}
              isCurrency={false}
            />
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-blue-800">Certificados Finales</h3>
          </div>
          <p className="text-2xl font-bold text-blue-700 relative overflow-hidden">
            <AnimatedNumberDisplay
              value={certificateEvolution[certificateEvolution.length - 1]?.totalCertificates || effectiveInitialCertificates}
              currencyFormat="MXN"
              duration={1.2}
              staggerDelay={0.08}
              isCurrency={false}
            />
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="font-medium text-purple-800">Por Reinversión</h3>
          </div>
          <p className="text-2xl font-bold text-purple-700 relative overflow-hidden">
            +<AnimatedNumberDisplay
              value={certificateEvolution[certificateEvolution.length - 1]?.certificatesFromReinvestment || 0}
              currencyFormat="MXN"
              duration={1.2}
              staggerDelay={0.08}
              isCurrency={false}
            />
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-orange-600" />
            <h3 className="font-medium text-orange-800">Utilidad Final</h3>
          </div>
          <p className="text-2xl font-bold text-orange-700 relative overflow-hidden">
            <AnimatedNumberDisplay
              value={convertToDisplayCurrency(certificateEvolution[certificateEvolution.length - 1]?.totalUtility || 0)}
              currencyFormat={investment.currencyFormat}
              duration={1.4}
              staggerDelay={0.1}
            />
          </p>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-green-600 sticky top-0 z-10">
              <tr>
                {selectedColumns.year && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.year}
                  </th>
                )}
                {selectedColumns.date && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.date}
                  </th>
                )}
                {selectedColumns.certificatePrice && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.certificatePrice}
                  </th>
                )}
                {selectedColumns.certificates && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.certificates}
                  </th>
                )}
                {selectedColumns.totalUtility && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.totalUtility}
                  </th>
                )}
                {selectedColumns.reinvestmentFund && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.reinvestmentFund}
                  </th>
                )}
                {selectedColumns.availableForReinvestment && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.availableForReinvestment}
                  </th>
                )}
                {selectedColumns.events && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.events}
                  </th>
                )}
                {selectedColumns.payments && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.payments}
                  </th>
                )}
                {selectedColumns.totalCertificates && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.totalCertificates}
                  </th>
                )}
                {selectedColumns.certificatesFromReinvestment && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.certificatesFromReinvestment}
                  </th>
                )}
                {selectedColumns.citrusPatrimony && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.citrusPatrimony}
                  </th>
                )}
                {selectedColumns.citrusIncome && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.citrusIncome}
                  </th>
                )}
                {selectedColumns.yearlyCashOutAmount && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.yearlyCashOutAmount}
                  </th>
                )}
                {selectedColumns.cumulativeCashOutAmount && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.cumulativeCashOutAmount}
                  </th>
                )}
                {selectedColumns.yearlyReinvestmentContribution && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.yearlyReinvestmentContribution}
                  </th>
                )}
                {selectedColumns.cumulativeReinvestmentContribution && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.cumulativeReinvestmentContribution}
                  </th>
                )}
                {selectedColumns.cumulativeTotalUtility && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.cumulativeTotalUtility}
                  </th>
                )}
                {selectedColumns.paymentBoostActive && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.paymentBoostActive}
                  </th>
                )}
                {selectedColumns.paymentBoostAmount && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    {content.excel.columns.paymentBoostAmount}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {certificateEvolution.map((row, index) => (
                <tr 
                  key={index}
                  className={`${row.newCertificateIds.length > 0 ? 'bg-green-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-25 transition-colors`}
                >
                  {selectedColumns.year && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.year}
                    </td>
                  )}
                  {selectedColumns.date && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {row.date}
                    </td>
                  )}
                  {selectedColumns.certificatePrice && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.certificatePrice), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.certificates && (
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1 min-w-[200px]">
                        {row.certificates
                          .sort((a, b) => a.id - b.id)
                          .map(cert => (
                            <div
                              key={cert.id}
                              className="relative group"
                              title={`Certificado ${cert.id}: ${
                                cert.status === 'reserved' ? 'En proceso de pago' : 
                                cert.status === 'waiting' ? 'En maduración' : 
                                cert.status === 'growing' ? 'Iniciando producción' : 
                                'Generando utilidades'
                              } - ${formatCurrency(convertToDisplayCurrency(cert.remainingPayment), investment.currencyFormat)} pendiente`}
                            >
                              {/* Certificate Badge */}
                              <div className={`inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-lg shadow-sm border-2 transition-all duration-300 group-hover:scale-110 ${
                                cert.status === 'reserved' ? 'bg-gradient-to-br from-cyan-400 to-cyan-600 text-white border-cyan-300' :
                                cert.status === 'waiting' ? 'bg-gradient-to-br from-gray-400 to-gray-600 text-white border-gray-300' :
                                cert.status === 'growing' ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white border-yellow-300' :
                                'bg-gradient-to-br from-green-400 to-green-600 text-white border-green-300'
                              }`}>
                                {cert.id}
                              </div>
                              
                              {/* Progress Bar for Reserved Certificates */}
                              {cert.status === 'reserved' && cert.initialPrice && (
                                <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-full transition-all duration-500"
                                    style={{ 
                                      width: `${Math.max(0, Math.min(100, ((cert.initialPrice - cert.remainingPayment) / cert.initialPrice) * 100))}%` 
                                    }}
                                  ></div>
                                </div>
                              )}
                              
                              {/* Status Indicator */}
                              <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm ${
                                cert.status === 'reserved' ? 'bg-cyan-500' :
                                cert.status === 'waiting' ? 'bg-gray-500' :
                                cert.status === 'growing' ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}></div>
                            </div>
                          ))}
                      </div>
                    </td>
                  )}
                  {selectedColumns.totalUtility && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.totalUtility), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.reinvestmentFund && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.reinvestmentFund), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.availableForReinvestment && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.availableForReinvestment), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.events && (
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        {row.newCertificateIds.map(id => (
                          <div key={`new-${id}`} className="text-green-600 font-medium text-xs">
                            ¡Certificado {id} liquidado completamente!
                          </div>
                        ))}
                        {row.reservedCertificateIds.map(id => (
                          <div key={`reserved-${id}`} className="text-teal-600 font-medium text-xs">
                            Apartado certificado {id}
                          </div>
                        ))}
                        {row.payments.length > 0 && (
                          <div className="text-gray-500 text-xs">
                            Pagos realizados: {row.payments.length}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {selectedColumns.payments && (
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        {row.payments.map((payment, paymentIndex) => (
                          <div key={paymentIndex} className="text-xs text-gray-600">
                            Cert. {payment.id}: {formatCurrency(convertToDisplayCurrency(payment.amount), investment.currencyFormat)}
                          </div>
                        ))}
                      </div>
                    </td>
                  )}
                  {selectedColumns.totalCertificates && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {row.totalCertificates}
                    </td>
                  )}
                  {selectedColumns.certificatesFromReinvestment && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {row.certificatesFromReinvestment}
                    </td>
                  )}
                  {selectedColumns.citrusPatrimony && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.citrusPatrimony), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.citrusIncome && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.citrusIncome), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.yearlyCashOutAmount && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.yearlyCashOutAmount), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.cumulativeCashOutAmount && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.cumulativeCashOutAmount), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.yearlyReinvestmentContribution && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.yearlyReinvestmentContribution), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.cumulativeReinvestmentContribution && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.cumulativeReinvestmentContribution), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.cumulativeTotalUtility && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(convertToDisplayCurrency(row.cumulativeTotalUtility), investment.currencyFormat)}
                    </td>
                  )}
                  {selectedColumns.paymentBoostActive && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {row.paymentBoostActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {currentLanguage === 'es' ? 'Sí' : currentLanguage === 'en' ? 'Yes' : 'Oui'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {currentLanguage === 'es' ? 'No' : currentLanguage === 'en' ? 'No' : 'Non'}
                        </span>
                      )}
                    </td>
                  )}
                  {selectedColumns.paymentBoostAmount && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {row.paymentBoostAmount > 0 ? 
                        formatCurrency(convertToDisplayCurrency(row.paymentBoostAmount), investment.currencyFormat) : 
                        '-'
                      }
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{content.excel.legend.title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-lg shadow-sm border-2 bg-gradient-to-br from-cyan-400 to-cyan-600 text-white border-cyan-300">
                1
              </div>
              <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-full" style={{ width: '60%' }}></div>
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm bg-cyan-500"></div>
            </div>
            <div>
              <div className="font-medium text-gray-800">{content.excel.legend.reserved}</div>
              <div className="text-sm text-gray-600">{content.excel.legend.reservedDesc}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-lg shadow-sm border-2 bg-gradient-to-br from-gray-400 to-gray-600 text-white border-gray-300">
                2
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm bg-gray-500"></div>
            </div>
            <div>
              <div className="font-medium text-gray-800">{content.excel.legend.waiting}</div>
              <div className="text-sm text-gray-600">{content.excel.legend.waitingDesc}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-lg shadow-sm border-2 bg-gradient-to-br from-yellow-400 to-yellow-600 text-white border-yellow-300">
                3
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm bg-yellow-500"></div>
            </div>
            <div>
              <div className="font-medium text-gray-800">{content.excel.legend.growing}</div>
              <div className="text-sm text-gray-600">{content.excel.legend.growingDesc}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-lg shadow-sm border-2 bg-gradient-to-br from-green-400 to-green-600 text-white border-green-300">
                4
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white shadow-sm bg-green-500"></div>
            </div>
            <div>
              <div className="font-medium text-gray-800">{content.excel.legend.producing}</div>
              <div className="text-sm text-gray-600">{content.excel.legend.producingDesc}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-4">{content.excel.notes.title}</h3>
        <ul className="list-disc pl-5 space-y-2 text-blue-700">
          <li>{content.excel.notes.maturationPeriod.replace('{period}', investment.isLongTermCalculator ? '6' : '5')}</li>
          <li>{content.excel.notes.incomeStart.replace('{year}', investment.isLongTermCalculator ? '6' : '5')}</li>
          <li>{content.excel.notes.appreciation.replace('{rate}', investment.appreciationRate.toString())}</li>
          {investment.increaseLemonPrice && (
            <li>{content.excel.notes.lemonPriceIncrease.replace('{rate}', investment.lemonPriceIncrease.toString())}</li>
          )}
          {!investment.increaseLemonPrice && (
            <li>{content.excel.notes.noLemonIncrease}</li>
          )}
          <li>{content.excel.notes.utilityCalculation
            .replace('{production}', investment.averageProductionPerHectare.toLocaleString())
            .replace('{price}', investment.averageSalePricePerKg.toString())}</li>
          {investment.reinvestProfits ? (
            <li>{content.excel.notes.reinvestmentActive}</li>
          ) : (
            <li>{content.excel.notes.reinvestmentInactive}</li>
          )}
          {investment.partialCashOut && (
            <li>{content.excel.notes.partialCashOut.replace('{percentage}', investment.cashOutPercentage.toString())}</li>
          )}
          {investment.enablePaymentBoost && (
            <li>{content.excel.notes.paymentBoost}</li>
          )}
          <li>{content.excel.notes.currency.replace('{currency}', investment.currencyFormat)}</li>
          {investment.currencyFormat !== 'MXN' && (
            <li>{content.excel.notes.exchangeRate
              .replace('{rate}', (investment.currencyFormat === 'USD' ? investment.exchangeRate : investment.exchangeRateEUR).toString())
              .replace('{currency}', investment.currencyFormat)}</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default ExcelTableV5;